import { Grid, IconButton, Box, TextField, Chip, Autocomplete, Typography, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import { CancelPresentationRounded, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from '../style';
import ModalDialog from '../../ui/ModalDialog';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EDIT_PERMISSION_KEYS, PERMISSION_USERS_CATEGORY, TOOLTIPS } from '../../../utils/constants';
import { createPermissions, editPermissions, listWellsUsers } from '../../../store/actions/permissions';
import { enqueueSnackbar } from '../../../store/actions/snackbar';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../confirmationModal';

const AddEditPermissionsModal = ({
  onCloseClick,
  isOpen,
  type,
  data,
  permissionsData,
  refreshParentDialog
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const [nonAdminUsers, setNonAdminUsers] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]);
  const [permissions, setPermissions] = useState(
    EDIT_PERMISSION_KEYS.reduce((acc, perm) => {
      acc[perm.value] = false;
      return acc;
    }, {})
  );
  const [isDirty, setIsDirty] = useState(false);
  const [saveWarning, setSaveWarning] = useState(false);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges([{ isDirty }], onCloseClick);
  const wellUsers = useSelector((state) => state.permissions.wellUsers);

  useEffect(() => {
    if (type === 'Edit' && data?.email) {
      setAddedUsers([data?.email === 'all_users' ? 'All Users' : data?.email]);
      setPermissions(data?.permissions);
    }
  }, []);

  useEffect(() => {
    if (!wellUsers.loading && !wellUsers.data.length) {
      dispatch(listWellsUsers());
    }
  }, []);

  useEffect(() => {
    if (wellUsers.data.length) {
      const existingEmails = permissionsData.map(item => item.email);
      const hasAllUsers = existingEmails.includes('all_users');
      const userEmails = wellUsers?.data
        .filter(email => !existingEmails.includes(email));

      setNonAdminUsers([
        ...(hasAllUsers ? [] : PERMISSION_USERS_CATEGORY),
        ...userEmails
      ]);
    }
  }, [wellUsers.loading]);

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const handlePermissionChange = (event) => {
    const { value, checked } = event.target;

    setPermissions((prev) => {
      const updated = {
        ...prev,
        [value]: checked
      };
      if (updated?.create) {
        return {
          read: true,
          update: true,
          create: true,
          delete: true
        };
      }
      const hasOtherPermissions = updated?.create || updated?.update || updated?.delete;
      if (hasOtherPermissions) {
        updated.read = true;
      }
      return updated;
    });
    setIsDirty(true);
  };

  const handlePermissionSave = () => {
    const permissionsBody = addedUsers.reduce((acc, email) => {
      const normalizedEmail = email === 'All Users' ? 'all_users' : email;

      acc[normalizedEmail] = { ...permissions };
      return acc;
    }, {});
    const payload = {
      permissions: permissionsBody,
      source: { collection: data?.collection?.collectionName, _id: data?._id }
    };
    onCloseClick();
    if (!permissionsData?.length) {
      dispatch(createPermissions(payload, refreshParentDialog));
    } else {
      dispatch(editPermissions(payload, data?.permissionId));
    }
    setIsDirty(false);
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {type} Permissions
        </Grid>
        <Grid item>
          <Tooltip title={TOOLTIPS.save}>
            <span>
              <IconButton
                variant="solid"
                onClick={() => {
                  if (!addedUsers?.length) {
                    dispatch(
                      enqueueSnackbar(
                        'Please add a user before saving',
                        'error',
                        new Date().getTime() + Math.random()
                      )
                    );
                    return;
                  }
                  if (data?.collection?.collectionName !== 'WellsInfos') {
                    setSaveWarning(true);
                    return;
                  }
                  handlePermissionSave();
                }}
                disabled={!isDirty}
              >
                <SaveRounded fontSize='large' />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid container p={2} flexDirection='column'>
        <Box flex={1}>
          <Typography variant='subtitle2' fontWeight='bold' mb={1}>Users</Typography>
          <Autocomplete
            loading={wellUsers.loading}
            disabled={type === 'Edit'}
            multiple
            options={nonAdminUsers}
            value={addedUsers}
            onChange={(event, values) => {
              setAddedUsers(values);
              setIsDirty(true);
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
                return;
              }
              setInputValue('');
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip variant="outlined" label={option} key={key} {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label=""
                placeholder={type === 'Add' ? 'Add Users' : ''}
              />
            )}
          />
        </Box>
        <Box
          display="grid"
          gridTemplateColumns="auto auto"
          justifyContent="center"
          columnGap={2}
          rowGap={1}
          mt={2}
        >
          {EDIT_PERMISSION_KEYS.map((perm) => {
            const hasOtherPermissions = permissions.create || permissions.update || permissions.delete;
            return (
            <FormControlLabel
              key={perm.value}
              sx={{ margin: 0 }}
              control={
                <Checkbox
                  value={perm.value}
                  checked={permissions[perm.value]}
                  onChange={handlePermissionChange}
                  disabled={(perm.value === 'read' && hasOtherPermissions) || (permissions.create && perm.value !== 'create') }
                />
              }
              label={perm.label}
              componentsProps={{
                typography: {
                  sx: {
                    fontSize: '16px',
                    fontWeight: 600
                  }
                }
              }}
            />
            );
          })}
        </Box>
      </Grid>
      <UnsavedChangesModal />
      {
        saveWarning && <ConfirmationModal
          confirmAction={false}
          open={saveWarning}
          handleAction={handlePermissionSave}
          handleClose={() => {
            setSaveWarning(false);
          }}
          modalLabel={`Permissions will only be effective for existing ${data?.collection?.label} dialog. Proceed to save?`}
        />
      }
    </ModalDialog>
  );
};
AddEditPermissionsModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  data: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  permissionsData: PropTypes.array,
  refreshParentDialog: PropTypes.func.isRequired
};
AddEditPermissionsModal.defaultProps = {
  type: 'Add',
  permissionsData: []
};
export default AddEditPermissionsModal;
